import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import Clipboard from 'react-clipboard.js';

import ShareIcon from './Svg/ShareIcon';
import SocialShareGroup from './SocialShareFooter/SocialShareGroup';

import { onFindYourRep } from '../containers/LocalRep/actions';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../utils/nextActionUtil';
import {
    closeShareDrawer,
    copyToClipboard,
    openShareDrawer,
    setOverlayVisible
} from '../containers/Widget/actions';

import { LocalRepState } from '../utils/enums';
import { width, widthEm, isPortrait } from '../utils/size';
import staticData from '../static_data/all';
import { copyFor } from '../copy/copy';

const Wrapper = styled.div`
    width: 100%;
    padding: 0 1em;
    display: flex;
    position: relative;
    justify-content: space-between;
    align-items: center;
    z-index: ${props => (props.shareDrawerVisibility ? '3' : '1')};
    background: ${props =>
        props.backgroundColor
            ? props.backgroundColor
            : props.hasNextAction
            ? '#f8f8f7'
            : '#fff'};

    &:after {
        content: '';
        width: 15px;
        height: 100%;
        background: linear-gradient(to right, #f8f8f7, #fff);
        position: absolute;
        right: 0;
        top: 0;
        display: ${props =>
            props.hasNextAction && !props.backgroundColor ? 'block' : 'none'};
    }

    &:before {
        content: '';
        width: 15px;
        height: 100%;
        background: linear-gradient(to left, #f8f8f7, #fff);
        position: absolute;
        left: 0;
        top: 0;
        display: ${props =>
            props.hasNextAction && !props.backgroundColor ? 'block' : 'none'};
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: ${props =>
        (props.localRepFound && !props.isEmailSubmitted) ||
        props.newsletterSignUpVisible ||
        props.useLocalRepresentative ||
        props.fullwidth
            ? props.rightAlign
                ? `flex-end`
                : `space-between`
            : `flex-end`};
    width: 100%;

    a {
        font-family: Lato;
        font-size: 0.625em;
        line-height: 1.6;
        color: #008eff;
        text-decoration: none;
        padding: 5px;
        white-space: nowrap;
        margin-left: 10px;
        cursor: pointer;

        svg {
            position: relative;
            top: 2px;
        }
    }
`;

const revealShareDrawerContent = y => keyframes`
    0% {
      transform: translateY(0)
    }

    100% {
      transform: translateY(${y})
    }
`;

const ShareDrawerContent = styled.div`
    position: absolute;
    left: -1em;
    width: calc(100% + 2em);
    padding: 16px 18px;
    background: #fff;
    z-index: 3;
    ${props => `transform: translateY(${props.drawerHeight})`};
    animation: ${props => revealShareDrawerContent(props.drawerHeight)} 0.3s linear
        alternate;
`;

const ShareSection = styled.div`
    width: 174px;
    float: left;

    @media (max-width: 30em) {
        width: 100%;
    }
`;

const EmbedSection = styled.div`
    width: calc(100% - 204px);
    float: left;
    margin-left: 30px;
    position: relative;

    @media (max-width: 30em) {
        width: 100%;
        margin-left: 0;
        margin-top: 12px;
    }
`;

const ShareBackground = styled.div`
    background: #000;
    position: fixed;
    left: 5px;
    top: 2px;
    width: calc(100% - 10px);
    height: calc(100% - 4px);
    transition: opacity 0.3s;
    opacity: 0;
    visibility: hidden;

    @media (max-width: 30em) {
        left: 0;
        width: 100%;
    }

    ${props =>
        props.shareDrawerVisibility &&
        `
            opacity: 0.5;
            visibility: visible;`};
`;

const Title = styled.h2`
    font-family: Averta;
    font-size: 16px;
    font-weight: normal;
    line-height: 1.63;
    color: #000000;
    margin: 0;
`;

const SocialShareContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;
    align-items: flex-start;
    margin-top: 8px;
`;

const Input = styled.input`
    border-radius: 2px;
    border: solid 1px #f1f1ee;
    width: 100%;
    height: 36px;
    line-height: 36px;
    font-family: Averta;
    font-size: 12px;
    margin-top: 8px;
    padding: 0 10px;
    appearance: none;

    @media (max-width: 30em) {
        height: 52px;
        line-height: 52px;
        font-size: 14px;
    }

    &:focus {
        outline: none;
        border: 1px solid #ff245e;
    }
`;

const Button = styled.div`
    position: absolute;
    right: -1px;
    bottom: 1px;
    height: 34px;
    line-height: 32px;
    padding: 0 10px;

    @media (max-width: 30em) {
        height: 50px;
        line-height: 50px;
    }

    &:before {
        content: '';
        width: 100px;
        height: 100%;
        float: left;
        background-image: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff);
    }

    button {
        border: 0;
        color: #ff245e;
        font-size: 14px;
        display: inline-block;
        background: #fff;
        cursor: pointer;
        font-weight: 600;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

        &:hover,
        &:focus {
            outline: none;
        }
    }
`;

const CloseButton = styled.div`
    width: 14px;
    height: 26px;
    transform: rotate(45deg);
    font-family: Averta;
    font-size: 24px;
    line-height: 1.08;
    text-align: left;
    color: #5d656c;
    margin-top: -0.625em;
    cursor: pointer;
    position: absolute;
    right: 18px;
    top: 28px;
`;

const Span = styled.div`
    font-size: 10px;
    color: #000;
    font-weight: bold;
    line-height: 8px;

    a {
        font-size: 10px;
        font-weight: normal;
        margin: 0 15px 0 5px;
        padding: 0;

        @media (max-width: 30em) {
            margin-left: 0;
        }

        &:last-child {
            margin-right: 0;
        }
    }
`;

const NoShareBottomDrawer = props => (
    <Wrapper
        shareDrawerVisibility={props.shareDrawerVisibility}
        backgroundColor={props.backgroundColor}
        hasNextAction={props.nextAction !== null}
    >
        <Container {...props}>
            {props.children}
            {props.localRepFound && props.useLocalRepresentative && (
                <Span>
                    {copyFor('findReps', 'notYour')}&nbsp;&nbsp;
                    <a onClick={props.onFindYourRep}>
                        {isPortrait()
                            ? copyFor('findReps', 'clickToFindShort')
                            : copyFor('findReps', 'clickToFind')}
                    </a>
                </Span>
            )}
            {props.isEndCard && !props.isEmailSubmitted && (
                <Span>
                    {copyFor('footer', 'stayInformed')}&nbsp;
                    {props.width < 350 && <br />}
                    <a onClick={() => props.setOverlayVisible(true)}>
                        {copyFor('footer', 'newsletterSignup')}
                    </a>
                </Span>
            )}
            <Span>
                <a
                    href="https://actionbutton.org/terms"
                    rel="noopener noreferrer"
                    target="_blank"
                    onClick={proxy => proxy.stopPropagation()}
                >
                    {copyFor('footer', 'terms')}
                </a>
            </Span>
        </Container>
    </Wrapper>
);

const ShareBottomDrawer = props => (
    <Wrapper
        shareDrawerVisibility={props.shareDrawerVisibility}
        backgroundColor={props.backgroundColor}
        hasNextAction={props.nextAction !== null}
    >
        <ShareBackground
            shareDrawerVisibility={props.shareDrawerVisibility}
            onClick={() => props.closeShareDrawer()}
        />
        {props.shareDrawerVisibility && (
            <ShareDrawerContent drawerHeight={widthEm() > 30 ? `-30px` : `-88px`}>
                <ShareSection>
                    <Title>{copyFor('footer', 'share')}</Title>
                    <SocialShareContainer>
                        <SocialShareGroup
                            twitterMention={props.twitterMention}
                            publisherName={props.publisherName}
                            partnerName={props.partnerName}
                            actionType={props.actionType}
                            actionShared={props.actionShared}
                        />
                    </SocialShareContainer>
                </ShareSection>
                <EmbedSection>
                    <Title>{copyFor('footer', 'embed')}</Title>
                    <Input id="embedIframe" defaultValue={props.iframeSnippet} readOnly />
                    <Button>
                        <Clipboard
                            data-clipboard-text={props.iframeSnippet}
                            onClick={() => props.copyToClipboard()}
                        >
                            {props.copiedToClipboard
                                ? copyFor('footer', 'copied')
                                : copyFor('footer', 'copy')}
                        </Clipboard>
                    </Button>
                </EmbedSection>
                <CloseButton onClick={() => props.closeShareDrawer()}>+</CloseButton>
            </ShareDrawerContent>
        )}
        <Container {...props}>
            {props.children}
            {!props.newsletterSignUpVisible &&
                props.localRepFound &&
                props.useLocalRepresentative && (
                    <Span>
                        {copyFor('findReps', 'notYour')}&nbsp;&nbsp;
                        <a onClick={props.onFindYourRep}>
                            {isPortrait()
                                ? copyFor('findReps', 'clickToFindShort')
                                : copyFor('findReps', 'clickToFind')}
                        </a>
                    </Span>
                )}
            {props.newsletterSignUpVisible && !props.isEmailSubmitted && (
                <Span>
                    <span>
                        {copyFor('footer', 'stayInformed')}
                        {isPortrait() && <br />}
                        <a onClick={() => props.setOverlayVisible(true)}>
                            {copyFor('footer', 'newsletterSignup')}
                        </a>
                    </span>
                </Span>
            )}
            <Span>
                <a onClick={() => props.openShareDrawer()}>
                    <ShareIcon /> {copyFor('footer', 'share')}
                </a>
                <a
                    href="https://actionbutton.org/terms"
                    rel="noopener noreferrer"
                    target="_blank"
                    onClick={proxy => proxy.stopPropagation()}
                >
                    {copyFor('footer', 'terms')}
                </a>
            </Span>
        </Container>
    </Wrapper>
);

const sharedProptypes = {
    twitterMention: PropTypes.string,
    publisherName: PropTypes.string,
    children: PropTypes.object,
    partnerName: PropTypes.string,
    actionType: PropTypes.string,
    actionShared: PropTypes.func,
    openShareDrawer: PropTypes.func,
    onFindYourRep: PropTypes.func.isRequired,
    closeShareDrawer: PropTypes.func,
    copyToClipboard: PropTypes.func,
    shareDrawerVisibility: PropTypes.bool,
    copiedToClipboard: PropTypes.bool,
    iframeSnippet: PropTypes.string,
    backgroundColor: PropTypes.string,
    nextAction: PropTypes.object,
    newsletterSignUpVisible: PropTypes.bool,
    localRepFound: PropTypes.bool,
    setOverlayVisible: PropTypes.func,
    isEmailSubmitted: PropTypes.bool,
    width: PropTypes.number.isRequired,
    useLocalRepresentative: PropTypes.bool.isRequired
};

NoShareBottomDrawer.propTypes = { ...sharedProptypes };
ShareBottomDrawer.propTypes = { ...sharedProptypes };

const ShareDrawer = staticData.useStatic ? NoShareBottomDrawer : ShareBottomDrawer;

export default connect(
    state => {
        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );

        return {
            nextAction,
            shareDrawerVisibility: state.widget.shareDrawerVisibility,
            copiedToClipboard: state.widget.textCopiedToClipboard,
            iframeSnippet: `<iframe style="width: 100%" frameborder="0" scrolling="no" height="450" src="https://embed.actionbutton.co/widget/widget-iframe.html?widgetId=${
                state.widget.widgetRequestId
            }"></iframe>`,
            isEmailSubmitted: state.widget.isEmailSubmitted,
            localRepFound: state.localRep.localRepData.representative !== undefined,
            useLocalRepresentative:
                !!state.widget.activeAction.localRepresentativeState &&
                state.widget.activeAction.localRepresentativeState !==
                    LocalRepState.disabled,
            width: width()
        };
    },
    {
        onFindYourRep,
        openShareDrawer,
        closeShareDrawer,
        copyToClipboard,
        setOverlayVisible
    }
)(ShareDrawer);



// WEBPACK FOOTER //
// ./src/components/ShareDrawer.jsx